import { useEffect, useState } from "react";
import { WhatsAppCampaignAPI, type WhatsAppVariableMapping } from "src/services/WhatsAppCampaignAPI";
import { PREVIEW_DEBOUNCE_MS } from "../WhatsAppCampaignConstants";
import type { WhatsAppPreviewState, WhatsAppTemplate } from "../WhatsAppCampaignTypes";

const IDLE_PREVIEW: WhatsAppPreviewState = {
  status: "idle",
  parameters: null,
  example: null,
  errors: [],
};

export const useWhatsAppPreview = (
  template: WhatsAppTemplate | null,
  listNames: string[],
  mappings: WhatsAppVariableMapping[]
) => {
  const [preview, setPreview] = useState(IDLE_PREVIEW);

  useEffect(() => {
    if (!template) {
      setPreview(IDLE_PREVIEW);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPreview(current => ({ ...current, status: "loading" }));
      WhatsAppCampaignAPI.fetchPreview(
        {
          template_name: template.name,
          language_code: template.language,
          list_names: listNames,
          variable_mappings: mappings,
        },
        controller.signal
      )
        .then(response => {
          if (controller.signal.aborted) {
            return;
          }
          setPreview(
            response.success
              ? {
                  status: "success",
                  parameters: response.parameters ?? null,
                  example: response.example ?? null,
                  errors: response.errors ?? [],
                }
              : { ...IDLE_PREVIEW, status: "error" }
          );
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setPreview({ ...IDLE_PREVIEW, status: "error" });
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [listNames, mappings, template]);

  return preview;
};
